import React from 'react'
import ReactDOM from 'react-dom'
import cx from 'classnames'
import Navigation from '/js/views/shared/components/navigation'
import Header from '/js/views/shared/components/header'
import DefaultClasses from '/js/util/defaultClasses'
import DataTable from '/js/views/shared/components/contentParticles/dataTable'
import logos from '/img/pages/appendix/appendix-logos.png'
import ArrowForward from '/img/svg/arrow'
import ArrowBack from '/img/svg/arrow-back'

class AppendixPageLayout extends React.Component {
  constructor(props) {
    super(props)
    this.containerRef = React.createRef()
  }

  componentDidMount() {
    if (!window.location.hash) {
      return
    }

    const target = this.containerRef.current.querySelector(window.location.hash)

    if (target) {
      target.scrollIntoView()
    }
  }

  renderPagination() {
    return (
      <div className="w-full flex flex-wrap justify-center mb-90">
        <a
          className={`flex-grow md:flex-grow-0 mr-15 ${DefaultClasses.button} ${DefaultClasses.buttonBlue}`}
          href='/foundational-elements'
        >
          <ArrowBack className="mr-10" />
          Foundational Elements
        </a>
        <a
          className={`flex-grow md:flex-grow-0 ${DefaultClasses.button} ${DefaultClasses.buttonBlue}`}
          href='/'
        >
          Home
          <ArrowForward className="ml-10" />
        </a>
      </div>
    )
  }

  renderParagraphs(section, sectionIndex) {
    const { theme } = this.props

    return section.paragraphs.map((paragraph, index) => {
      if (Object.keys(paragraph)[0] === 'dataTable') {
        return (
          <DataTable
            content={paragraph.dataTable}
            theme={theme}
            key={`data-table-${index}`}
          />
        )
      }
    })
  }

  renderSections() {
    const { theme, pageData } = this.props
    const { sections, intro } = pageData
    const introExists = !!intro

    return sections.map((section, index) => {
      return (
        <div
          id={section.id || 'missing-id'}
          className={cx('pt-40 w-full', {
            'border-t border-columbia-gray': introExists,
          })}
          key={`letter-section-${index}`}
        >
          {section.title && (
            <h2
              className={`${theme.textColor} text-18 xl:text-24 border-b-4 mb-30 ${theme.borderColor}`}
            >
              {section.title}
            </h2>
          )}
          {this.renderParagraphs(section, index)}
        </div>
      )
    })
  }

  render() {
    const { shared, currentPage, routes, pageData, theme } = this.props

    return (
      <>
        <Navigation
          navigation={shared.navigation}
          social={shared.social}
          currentPage={currentPage}
          routes={routes}
          theme={theme}
        />
        <Header
          header={shared.header}
          theme={theme}
          currentPage={currentPage}
          routes={routes}
        />

        <div data-label="letter-page-layout">
          <section data-label="hero" className="bg-white">
            <div className="container px-25">
              <div
                data-label="hero-container"
                className="flex py-55 md:pt-90 md:pb-75 border-b-4 border-smoke-white"
              >
                <div className="text-center md:text-left mb-45 md:mb-0">
                  <h1
                    data-label="hero-title"
                    className="font-serif text-36 md:text-64 tracking-tight leading-tight pb-16 mb-40 border-b-4 border-smoke-white"
                  >
                    About This Report
                  </h1>
                  <div className="text-left w-full lg:w-2/3 lg:ml-1/3 md:pl-30">
                    <ul className="list-disc lg:pr-20 pl-40">
                      <li className="mb-16">
                        Certain statements included or incorporated by reference
                        in this report are “forward-looking statements” within
                        the meaning of the United States federal securities
                        laws. All statements other than historical factual
                        information are forward-looking statements.
                        Forward-looking statements are based on assumptions and
                        assessments made by our management in light of their
                        experience and perceptions of historical trends, current
                        conditions, expected future developments and other
                        factors. Forwardlooking statements are not guarantees of
                        future performance and actual results may differ
                        materially from the results, developments and business
                        decisions contemplated by our forwardlooking statements.
                        Accordingly, you should not place undue reliance on any
                        such forwardlooking statements. Important factors that
                        in some cases have affected us in the past and that in
                        the future could cause actual results to differ
                        materially from those envisaged in our forwardlooking
                        statements are described in Danaher’s filings with the
                        United States Securities and Exchange Commission. The
                        forward-looking statements included in this report speak
                        only as of the date of this report, and except to the
                        extent required by applicable law, we do not assume any
                        obligation to update or revise any forward-looking
                        statement, whether as a result of new information,
                        future events and developments or otherwise.
                      </li>
                      <li className="mb-16">
                        In this report, we describe certain products and devices
                        that have applications submitted and pending for certain
                        regulatory approvals and/or are available only in
                        certain markets.
                      </li>
                    </ul>
                    <ul className="list-disc lg:pr-20 pl-40">
                      <li className="mb-16">
                        Any trademarks, product names or brand images appearing
                        herein are the property of their respective owners.
                      </li>
                      <li className="mb-16">
                        We refer to developing markets as high-growth markets in
                        our financial reporting and define them as geographic
                        markets experiencing extended periods of accelerated
                        growth in gross domestic product and infrastructure,
                        which include Eastern Europe, the Middle East, Africa,
                        Latin America and Asia (except Japan, Australia and New
                        Zealand).
                      </li>
                      <li className="mb-16">
                        This report is dated as of October 15, 2020 and unless
                        otherwise noted, all data in this report is as of
                        October 15, 2020. All financial information in this
                        report is limited to continuing operations and is
                        reported in U.S. dollars.
                      </li>
                      <li className="mb-16">
                        Uncertainties are inherent in collecting data from a
                        wide range of facilities and operations in a global
                        company such as Danaher. The data included in this
                        report (other than audited financial data) are good
                        faith estimates and have not been externally assured. We
                        expect our data collection systems to evolve and we will
                        seek to continually improve our processes for collecting
                        and disclosing accurate, meaningful and consistent data.
                      </li>
                    </ul>
                    <ul className="list-disc lg:pr-20 pl-40">
                      <li className="mb-16">
                        Danaher applied the following methodology with respect
                        to the metrics and goals included in this report
                        relating to energy usage, GHG emissions, water usage and
                        waste generation and diversion from landfills or
                        incinerators (“environmental metrics”). We collected
                        2019 data from (1) Danaher locations owned or leased
                        from January 1, 2019 through December 31, 2019 that in
                        aggregate account for approximately 75% of our total
                        space, and (2) vehicles and aircraft owned or leased by
                        Danaher during 2019. With respect to any locations owned
                        or leased from January 1, 2019 through December 31, 2019
                        for which data was not collected, we endeavored to
                        account for such locations by extrapolation.
                        <ul className="list-circle pl-20 pt-20">
                          <li className="mb-10">
                            The energy usage and GHG emissions metrics are based
                            on good faith estimates of fuel consumed on-site,
                            purchased energy and energy consumed through
                            operation of Danaher-owned or -leased vehicles and
                            aircraft. Danaher’s non-energy-consumption GHG
                            emissions, such as refrigerant releases and process
                            air emissions, are omitted. With respect to GHG
                            emissions, we take into account offsets in
                            calculating such emissions provided such offsets are
                            recognized by an accrediting organization recognized
                            by the CDP.
                          </li>
                          <li className="mb-10">
                            We define “hazardous/regulated” as wastes that are
                            deemed regulated or hazardous by national
                            legislation/regulations at the point of generation.
                          </li>
                          <li className="mb-10">
                            When normalizing the environmental metrics to
                            revenue for a particular year, we do not include
                            revenue attributable to acquisitions consummated
                            subsequent to January 1 of such year.
                          </li>
                        </ul>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </section>
          <section data-label="hero" className="bg-white">
            <div className="container px-25">
              <div
                data-label="hero-container"
                className="py-55 md:pt-90 md:pb-75 border-b-4 border-smoke-white"
              >
                <div className="text-center md:text-left mb-45">
                  <h1
                    data-label="hero-title"
                    className="font-serif text-36 md:text-64 tracking-tight leading-tight mb-32"
                  >
                    Reporting Frameworks
                  </h1>
                  <p>
                    As part of our efforts to enhance the transparency and
                    accountability of our sustainability program, this report
                    contains disclosures that address elements of the{' '}
                    <a
                      className="italic underline"
                      href="https://www.globalreporting.org/"
                      target="_blank"
                      rel="noreferrer"
                    >
                      Global Reporting Initiative™
                    </a>{' '}
                    (GRI) Sustainability Reporting Guidelines, the{' '}
                    <a
                      className="italic underline"
                      href="https://www.sasb.org/"
                      target="_blank"
                      rel="noreferrer"
                    >
                      Sustainability Accounting Standards Board
                    </a>{' '}
                    (SASB) Medical Equipment and Supplies Sustainability
                    Accounting Standard, the{' '}
                    <a
                      className="italic underline"
                      href="https://www.fsb-tcfd.org/"
                      target="_blank"
                      rel="noreferrer"
                    >
                      Task Force on Climate-related Financial Disclosures
                    </a>{' '}
                    (TCFD)and the{' '}
                    <a
                      className="italic underline"
                      href="https://www.un.org/sustainabledevelopment/sustainable-development-goals/"
                      target="_blank"
                      rel="noreferrer"
                    >
                      United Nations Sustainable Development Goals
                    </a>{' '}
                    (UN SDGs). With respect to each such standard, while this
                    report is not intended to meet all the requirements of the
                    standard, we have referenced each relevant section of the
                    standard as to which we believe full or partial responsive
                    information has been provided.
                  </p>
                </div>
                <img src={logos} className="mx-auto" alt="" />
              </div>
            </div>
          </section>

          <div className="container px-20 pt-40 pb-100" ref={this.containerRef}>
            <div className="w-full">{this.renderSections()}</div>
            {this.renderPagination()}
          </div>
        </div>
      </>
    )
  }
}

export default AppendixPageLayout
